import React from 'react'
import { inject, observer } from 'mobx-react'
import { Button, Checkbox, DatePicker, Dropdown, Icon, Input, Menu, Modal, Pagination, Spin, Table } from 'antd'
import moment from 'moment'
import { Recipients } from './Recipients'
import { cancellationRequests } from '@services/api'
import { alertPopup } from '@utils/popup'
import {
    CANCELLATION_REASON_TYPES,
    CANCELLATION_STATUS_COLORS,
    CANCELLATION_STATUS_TYPES,
    FUTURE_CANCELLATION_DATE_LABEL,
    SERVICE_PROVIDED_DATE_LABEL
} from '@texts/cancellation'
import * as styleClass from './style.scss'
import EditStatus from './EditStatus'
import {
    awaitingPaymentPayload,
    awaitingPaymentState,
    canEditCancelExplained,
    cancelExplainedPayload,
    canStartAwaitingPayment,
    isCancelExplained,
    canSubmitAwaitingPayment,
    requiresPayment,
    serviceDateLabel
} from './utils'

const CANCELLATION_TABLE_COLUMN_WIDTHS = {
    created_at: 120,
    updated_at: 120,
    client_id: 150,
    created_by: 150,
    agent: 150,
    parent_agent: 150,
    messages: 90,
    subscription: 160,
    reason: 190,
    sub_closed_at: 190,
    status: 230,
    in_legal_proceedings: 110,
    is_cancel_explained: 110,
    is_payment_required: 170,
    updated_by: 120,
    recipients: 100,
    new_commission: 110
}

// Shown before a payment-required row moves to FUTURE_CANCELLATION, which drops the payment requirement.
const PAYMENT_WAIVER_CONFIRM = 'האם את/ה בטוח/ה שברצונך להעביר את הבקשה לביטול עתידי? פעולה זו תבטל את דרישת התשלום.'

const CANCELLATION_TABLE_SCROLL_X = Object.values(CANCELLATION_TABLE_COLUMN_WIDTHS).reduce((a, b) => a + b, 0)
const CANCELLATION_TABLE_SCROLL_X_WITHOUT_ADMIN =
    CANCELLATION_TABLE_SCROLL_X - CANCELLATION_TABLE_COLUMN_WIDTHS.new_commission

// 400px is the page header, the filter bar with its button row above the table, the pagination
// line below the table, and the table's own header. calc keeps it viewport-relative, so the
// table follows a window resize without a listener. rc-table applies scroll.y as maxHeight,
// so a page with only a few rows stays short instead of leaving empty space below.
// The 300px floor matches what the old JS getTableHeight() enforced: below roughly a 620px
// viewport the calc alone leaves the table body unusably short. If a browser lacks CSS max()
// the whole maxHeight is dropped and the table grows with the page, i.e. the pre-change
// behaviour, so the failure mode is benign.
const CANCELLATION_TABLE_SCROLL_Y = 'max(300px, calc(100vh - 400px))'

@inject((store: any) => {
    const { popupStore, authStore, cancellationRequestStore } = store
    return {
        popupStore,
        authStore,
        cancellationRequestStore
    }
})
@observer
export default class CancellationTable extends React.Component<any, any> {
    constructor(props) {
        super(props)
        this.state = {
            recipientData: { requestId: '', recipients: [] },
            show: false,
            newDate: '',
            editDate: null,
            clearing: null,
            editReason: null,
            statusSpin: null,
            legalSpin: null,
            // row id -> true while its "הוסבר ללקוח" save runs (several rows may save at once)
            explainSaving: {},
            editFutureDate: null,
            newFutureDate: '',
            futureSpin: null,
            pendingStatus: null,
            selectedReason: null,
            payingId: null,
            paymentDate: '',
            paymentSpin: null,
            ...awaitingPaymentState()
        }
    }

    safeParse = (value, fallback) => {
        if (!value) return fallback
        if (typeof value !== 'string') return value
        try {
            return JSON.parse(value)
        } catch (e) {
            return fallback
        }
    }

    getRecipients = record => {
        const parsed = this.safeParse(record.recipients, { recipients: [] })
        return parsed && Array.isArray(parsed.recipients) ? parsed.recipients : []
    }

    getRowKey = (record, index) =>
        record.id ||
        record.request_id ||
        `${record.requested_for || 'request'}-${record.created_at || 'created'}-${record.subscription_id ||
            record.special_subscription_id ||
            index}`

    // Person links open in a new tab so an operator can check an agent or client without
    // losing their place in a filtered list. A plain anchor with target=_blank is used
    // instead of NavLink or window.open, so middle-click and ctrl-click keep working.
    renderPersonLink = (path, id, firstName, lastName) => {
        if (!id) return <div>{'-'}</div>

        const name = `${firstName || ''} ${lastName || ''}`.trim()
        return (
            <a
                href={`${path}/${id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', flexDirection: 'column' }}
            >
                <span>{name || '-'}</span>
                <span>{id}</span>
            </a>
        )
    }

    renderSystemAuthor = record => {
        if (!record.created_by) return <div>{'-'}</div>

        const joinedName = `${record.created_by_first_name || ''} ${record.created_by_last_name || ''}`.trim()
        const name = joinedName || record.created_by_system_label || 'מערכת'

        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{name}</span>
                <span>{record.created_by}</span>
            </div>
        )
    }

    handleStatusSelect = (requestStatus, requestId) => {
        const {
            cancellationRequestStore: { updateListLocally, getCancellationRequests }
        } = this.props

        this.setState({ statusSpin: requestId, editFutureDate: null, newFutureDate: '', pendingStatus: null }, () => {
            cancellationRequests
                .updateCancellationRequest({ requestId, requestStatus })
                .then(res => {
                    updateListLocally({ request: res.data.cancellation_request })
                    this.setState({ statusSpin: null })
                    getCancellationRequests()
                })
                .catch(error => {
                    this.setState({ statusSpin: null })
                    this.props.cancellationRequestStore.refreshOnConflict(error)
                    alertPopup({ type: 'error', title: 'אירעה שגיאה', body: 'העדכון נכשל' })
                })
        })
    }

    handleLegalToggle = (record, checked) => {
        const {
            cancellationRequestStore: { updateListLocally, getCancellationRequests }
        } = this.props
        this.setState({ legalSpin: record.id }, () => {
            cancellationRequests
                .updateCancellationRequest({ requestId: record.id, in_legal_proceedings: checked ? 1 : 0 })
                .then(res => {
                    updateListLocally({ request: res.data.cancellation_request })
                    this.setState({ legalSpin: null })
                    getCancellationRequests()
                })
                .catch(error => {
                    this.setState({ legalSpin: null })
                    this.props.cancellationRequestStore.refreshOnConflict(error)
                    alertPopup({ type: 'error', title: 'אירעה שגיאה', body: 'העדכון נכשל' })
                })
        })
    }

    // One save per row at a time; the checkbox shows the stored value until the server answers, so a failed save
    // leaves it as it was. Success patches the row from the response.
    handleExplainedToggle = (record, checked) => {
        const {
            cancellationRequestStore: { updateListLocally, refreshOnConflict }
        } = this.props
        if (this.state.explainSaving[record.id]) return
        const setSaving = saving =>
            this.setState(({ explainSaving }) => ({ explainSaving: { ...explainSaving, [record.id]: saving } }))

        setSaving(true)
        cancellationRequests
            .updateCancellationRequest(cancelExplainedPayload(record.id, checked))
            .then(res => {
                updateListLocally({ request: res.data.cancellation_request })
                setSaving(false)
            })
            .catch(error => {
                setSaving(false)
                refreshOnConflict(error)
                alertPopup({ type: 'error', title: 'אירעה שגיאה', body: 'העדכון נכשל' })
            })
    }

    getValidFutureDate = value => {
        if (!value) return null
        const date = moment(value)
        return date.isValid() ? date : null
    }

    isFutureDate = value => {
        const date = this.getValidFutureDate(value)
        return !!date && date.isAfter(moment().endOf('day'))
    }

    canClearFutureDate = record =>
        record.status !== 'FUTURE_CANCELLATION' &&
        this.state.pendingStatus !== 'FUTURE_CANCELLATION' &&
        !requiresPayment(record)

    openFutureEdit = (record, pendingStatus = null) => {
        const futureDate = this.getValidFutureDate(record.future_cancellation_date)
        this.setState({
            editFutureDate: record.id,
            pendingStatus,
            newFutureDate: futureDate ? futureDate.format('YYYY-MM-DD 00:00:00') : ''
        })
    }

    cancelFutureEdit = () => {
        this.setState({ editFutureDate: null, newFutureDate: '', pendingStatus: null })
    }

    saveFutureDate = record => {
        const { newFutureDate, pendingStatus } = this.state
        const isClearing = !newFutureDate
        if (!isClearing && !this.isFutureDate(newFutureDate)) return
        if (isClearing && !this.canClearFutureDate(record)) return

        const payload: any = {
            requestId: record.id,
            future_cancellation_date: isClearing ? null : newFutureDate
        }
        if (pendingStatus) payload.requestStatus = pendingStatus

        if (pendingStatus === 'FUTURE_CANCELLATION' && Number(record.is_payment_required) === 1) {
            Modal.confirm({
                title: 'ביטול עתידי',
                content: <div style={{ direction: 'rtl', textAlign: 'right' }}>{PAYMENT_WAIVER_CONFIRM}</div>,
                okText: 'אישור',
                cancelText: 'ביטול',
                onOk: () => this.sendFutureDate(payload, record)
            })
            return
        }
        this.sendFutureDate(payload, record)
    }

    sendFutureDate = (payload, record) => {
        const {
            cancellationRequestStore: { updateListLocally, getCancellationRequests, refreshOnConflict }
        } = this.props
        this.setState({ futureSpin: record.id }, () => {
            cancellationRequests
                .updateCancellationRequest(payload)
                .then(res => {
                    updateListLocally({ request: res.data.cancellation_request })
                    this.setState({
                        futureSpin: null,
                        editFutureDate: null,
                        newFutureDate: '',
                        pendingStatus: null
                    })
                    getCancellationRequests()
                })
                .catch(error => {
                    this.setState({ futureSpin: null })
                    if (refreshOnConflict(error)) this.cancelFutureEdit()
                    alertPopup({ type: 'error', title: 'אירעה שגיאה', body: 'העדכון נכשל' })
                })
        })
    }

    // OPEN, HANDLE_REQUIRE or FUTURE_CANCELLATION -> AWAITING_PAYMENT: needs a future date, the actual service date
    // (saved as service_provided_date) and documentation. Sends no email or SMS. Opening prefills from the row
    // (a FUTURE_CANCELLATION row's scheduled date, a stored service date). Eligibility, validation and payload: ./utils.
    openAwaitingPayment = record => {
        this.setState(awaitingPaymentState(record))
    }

    closeAwaitingPayment = () => {
        this.setState(awaitingPaymentState())
    }

    canSaveAwaitingPayment = () => canSubmitAwaitingPayment(this.state, this.isFutureDate)

    saveAwaitingPayment = () => {
        const {
            cancellationRequestStore: { updateListLocally, getCancellationRequests, refreshOnConflict }
        } = this.props
        if (!this.state.awaitingRecord || !this.canSaveAwaitingPayment()) return

        const payload = awaitingPaymentPayload(this.state)
        this.setState({ awaitingSpin: true }, () => {
            cancellationRequests
                .updateCancellationRequest(payload)
                .then(res => {
                    updateListLocally({ request: res.data.cancellation_request })
                    this.closeAwaitingPayment()
                    getCancellationRequests()
                })
                .catch(error => {
                    this.setState({ awaitingSpin: false })
                    const key = error && error.response && error.response.data && error.response.data.key
                    if (refreshOnConflict(error) || key === 'PAYMENT_ALREADY_COMPLETED') {
                        // The row is no longer an eligible unpaid request: drop the modal and show the current state.
                        this.closeAwaitingPayment()
                        if (key === 'PAYMENT_ALREADY_COMPLETED') getCancellationRequests()
                    }
                    alertPopup({ type: 'error', title: 'אירעה שגיאה', body: 'העדכון נכשל' })
                })
        })
    }

    // Ticking "התקבל תשלום" pre-fills today; the operator can still pick an earlier date.
    openPaymentEdit = (record, checked) => {
        this.setState({
            payingId: checked ? record.id : null,
            paymentDate: checked ? moment().format('YYYY-MM-DD 00:00:00') : ''
        })
    }

    isValidPaymentDate = value => {
        const date = value ? moment(value) : null
        return !!date && date.isValid() && !date.isAfter(moment(), 'day')
    }

    savePayment = record => {
        const {
            cancellationRequestStore: { updateListLocally, getCancellationRequests }
        } = this.props
        const { paymentDate } = this.state
        if (!paymentDate) return
        if (!this.isValidPaymentDate(paymentDate)) {
            alertPopup({ type: 'error', title: 'תאריך התשלום לא יכול להיות עתידי', body: '' })
            return
        }

        // One call: the server sets is_payment_required=false, awaiting_paid_at and status=OPEN together.
        const payload = {
            requestId: record.id,
            is_payment_required: false,
            awaiting_paid_at: paymentDate,
            requestStatus: 'OPEN'
        }
        this.setState({ paymentSpin: record.id }, () => {
            cancellationRequests
                .updateCancellationRequest(payload)
                .then(res => {
                    updateListLocally({ request: res.data.cancellation_request })
                    this.setState({ paymentSpin: null, payingId: null, paymentDate: '' })
                    getCancellationRequests()
                })
                .catch(err => {
                    const key = err && err.response && err.response.data && err.response.data.key
                    this.setState({ paymentSpin: null })
                    if (key === 'PAYMENT_NOT_REQUIRED') {
                        // Someone else already recorded it: drop the editor and show the current row.
                        this.setState({ payingId: null, paymentDate: '' })
                        alertPopup({ type: 'alert', title: 'התשלום כבר עודכן', body: '' })
                        getCancellationRequests()
                    } else if (key === 'PAID_AT_IN_FUTURE') {
                        alertPopup({ type: 'error', title: 'תאריך התשלום לא יכול להיות עתידי', body: '' })
                    } else {
                        alertPopup({ type: 'error', title: 'אירעה שגיאה', body: 'העדכון נכשל' })
                    }
                })
        })
    }

    updateRecipients = (values, requestId) => {
        this.setState({ recipientData: { requestId, recipients: values } })
    }

    sendRecipients = () => {
        const {
            cancellationRequestStore: { updateCancellationRequest }
        } = this.props

        const {
            recipientData: { requestId, recipients }
        } = this.state

        updateCancellationRequest({ requestId, recipients })
    }

    getCancellationDate = record =>
        record.cancelled_at
            ? moment(record.cancelled_at).format('YYYY-MM-DD 00:00:00')
            : moment().format('YYYY-MM-DD 00:00:00')

    openDateEdit = record => {
        this.setState({ editDate: record.id, newDate: this.getCancellationDate(record) })
    }

    cancelDateEdit = () => {
        this.setState({ editDate: null, newDate: '' })
    }

    sendNewDate = requestId => {
        const {
            cancellationRequestStore: { getCancellationRequests }
        } = this.props
        // empty newDate (X was clicked) sends null
        const cancelled_at = this.state.newDate || null

        // show the loader on this row while the request runs
        this.setState({ clearing: requestId })

        //using service, not store function, in order to then()
        cancellationRequests
            .updateCancellationRequest({ requestId, cancelled_at })
            .then(() => {
                // keep the loader until the refetch lands, otherwise the stale old date flashes
                return Promise.resolve(getCancellationRequests()).then(() => {
                    this.setState({ editDate: null, newDate: '', clearing: null })
                })
            })
            .catch(error => {
                this.setState({ clearing: null })
                if (this.props.cancellationRequestStore.refreshOnConflict(error)) this.cancelDateEdit()
                alertPopup({ type: 'error', title: 'אירעה שגיאה', body: 'העדכון נכשל' })
            })
    }

    columns = [
        {
            title: 'נוצר בתאריך',
            key: 'created_at',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.created_at,
            align: 'right',
            render: (_, record) => {
                let [date, time] = moment(record.created_at)
                    .format('DD/MM/YYYY - HH:mm')
                    .split(' - ')
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{date}</span>
                        <span>{time}</span>
                    </div>
                )
            }
        },
        {
            title: 'עודכן בתאריך',
            key: 'updated_at',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.updated_at,
            align: 'right',
            render: (_, record) => {
                let [date, time] = moment(record.updated_at)
                    .format('DD/MM/YYYY - HH:mm')
                    .split(' - ')

                return record.updated_at !== record.created_at ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{date}</span>
                        <span>{time}</span>
                    </div>
                ) : (
                    '-'
                )
            }
        },
        {
            title: 'לקוח',
            key: 'client_id',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.client_id,
            align: 'right',
            render: (_, record) =>
                this.renderPersonLink(
                    '/clients',
                    record.requested_for,
                    record.requested_for_first_name,
                    record.requested_for_last_name
                )
        },
        {
            title: 'נוצר ע״י',
            key: 'created_by',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.created_by,
            align: 'right',
            render: (_, record) =>
                Number(record.created_by_is_system) === 1
                    ? this.renderSystemAuthor(record)
                    : this.renderPersonLink(
                          '/agents',
                          record.created_by,
                          record.created_by_first_name,
                          record.created_by_last_name
                      )
        },
        {
            title: 'סוכן',
            key: 'agent',
            align: 'right',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.agent,
            render: (_, record) =>
                this.renderPersonLink('/agents', record.agent_id, record.agent_first_name, record.agent_last_name)
        },
        {
            title: 'סוכן אב',
            key: 'parent_agent',
            align: 'right',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.parent_agent,
            render: (_, record) =>
                this.renderPersonLink(
                    '/agents',
                    record.parent_agent_id,
                    record.parent_agent_first_name,
                    record.parent_agent_last_name
                )
        },
        {
            title: 'תיעוד',
            key: 'messages',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.messages,
            align: 'right',
            render: (_, record) => {
                const {
                    authStore: { id }
                } = this.props

                let userIsRecipient = null
                const recipients = this.getRecipients(record)
                userIsRecipient = recipients.find(recId => `${id}` === `${recId}`)

                return (
                    <Button
                        onClick={() => {
                            this.props.popupStore.togglePopup('CancellationRequestMessages', {
                                messages: this.safeParse(record.messages, { messages: [] }),
                                id: record.id,
                                file: record.file,
                                userIsRecipient
                            })
                        }}
                    >
                        {'תיעוד'}
                    </Button>
                )
            }
        },
        {
            title: 'סוג המינוי',
            key: 'subscription',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.subscription,
            align: 'right',
            render: (_, record) => (
                <div>{`${record.subscription_name || '—'} ₪${record.subscription_price ||
                    record.special_subscription_price}`}</div>
            )
        },
        {
            title: 'סיבת הבקשה',
            key: 'reason',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.reason,
            align: 'right',
            render: (_, record) => {
                const {
                    authStore: { roles }
                } = this.props
                const { editReason } = this.state
                const safeRoles = roles || []

                if (safeRoles.includes('admin') || safeRoles.includes('money')) {
                    if (editReason !== record.id) {
                        return (
                            <div style={{ display: 'flex' }}>
                                <div>{CANCELLATION_REASON_TYPES[record.request_reason]}</div>
                                <span
                                    onClick={() =>
                                        this.setState({ editReason: record.id, selectedReason: record.request_reason })
                                    }
                                    style={{ marginRight: '7px', cursor: 'pointer' }}
                                >
                                    <Icon type="edit" />
                                </span>
                            </div>
                        )
                    } else {
                        return (
                            <EditStatus
                                cancel={() => this.setState({ editReason: null })}
                                recordId={record.id}
                                requestReason={record.request_reason}
                            />
                        )
                    }
                } else {
                    return <div>{CANCELLATION_REASON_TYPES[record.request_reason]}</div>
                }
            }
        },
        {
            title: 'תאריך תשלום אחרון',
            key: 'sub_closed_at',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.sub_closed_at,
            align: 'right',
            render: (_, record) => {
                const {
                    authStore: { roles }
                } = this.props
                const { editDate, newDate, clearing } = this.state
                const safeRoles = roles || []

                const allowEdit =
                    safeRoles.includes('admin') || safeRoles.includes('moderator') || safeRoles.includes('money')

                if (allowEdit) {
                    if (!record.cancelled_at && editDate !== record.id) {
                        return (
                            <div
                                style={{ display: 'flex', cursor: 'pointer' }}
                                onClick={() => this.openDateEdit(record)}
                            >
                                <div>עדכון ביטול מינוי</div>
                                <span style={{ marginRight: '7px', cursor: 'pointer' }}>
                                    <Icon type="edit" />
                                </span>
                            </div>
                        )
                    } else if (editDate === record.id /*&& record.cancelled_at*/) {
                        // while the clear/save request runs, show a loader instead of the date
                        if (clearing === record.id) {
                            return <Spin />
                        }
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <DatePicker
                                        format={'DD/MM/YYYY'}
                                        value={newDate ? moment(newDate) : null}
                                        onChange={e =>
                                            this.setState({ newDate: e ? e.format('YYYY-MM-DD 00:00:00') : '' })
                                        }
                                        disabledDate={current => !!(current && current > moment().endOf('day'))}
                                        allowClear={false}
                                    />
                                    {/* X clears the date in state only; null is sent on Save. Only shown when a date exists */}
                                    {newDate && (
                                        <Icon
                                            type="close"
                                            className="clear-date-x"
                                            onClick={() => this.setState({ newDate: '' })}
                                        />
                                    )}
                                </div>
                                <div style={{ alignSelf: 'end' }}>
                                    <span style={{ cursor: 'pointer' }} onClick={this.cancelDateEdit}>
                                        ביטול
                                    </span>
                                    <span
                                        style={{ marginRight: '7px', cursor: 'pointer' }}
                                        onClick={() => this.sendNewDate(record.id)}
                                    >
                                        שמירה
                                    </span>
                                </div>
                            </div>
                        )
                    } else if (editDate !== record.id) {
                        let [date] = moment(record.cancelled_at)
                            .format('DD/MM/YYYY - HH:mm')
                            .split(' - ')
                        return (
                            <div
                                onClick={() => this.openDateEdit(record)}
                                style={{ display: 'flex', cursor: 'pointer' }}
                            >
                                <div>{date}</div>
                                <span style={{ marginRight: '7px', cursor: 'pointer' }}>
                                    <Icon type="edit" />
                                </span>
                            </div>
                        )
                    }
                } else {
                    if (record.cancelled_at) {
                        let [date] = moment(record.cancelled_at)
                            .format('DD/MM/YYYY - HH:mm')
                            .split(' - ')
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span>{date}</span>
                            </div>
                        )
                    } else {
                        return null
                    }
                }
            }
        },
        {
            title: 'סטטוס ',
            key: 'status',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.status,
            align: 'right',
            render: (_, record) => {
                const safeRoles = this.props.authStore.roles || []
                const isAgentsModerator = safeRoles.includes('agents_moderator')
                const isPaymentRestricted = requiresPayment(record)
                const allowEdit =
                    safeRoles.includes('admin') ||
                    safeRoles.includes('moderator') ||
                    safeRoles.includes('money') ||
                    isAgentsModerator
                const futureCancellationDate = this.getValidFutureDate(record.future_cancellation_date)
                const selectedFutureDate = this.getValidFutureDate(this.state.newFutureDate)
                const isEditingFutureDate = this.state.editFutureDate === record.id
                const hasExistingFutureDate = !!this.getValidFutureDate(record.future_cancellation_date)
                const isClearing = !this.state.newFutureDate && this.canClearFutureDate(record) && hasExistingFutureDate
                const canSaveFutureDate = this.isFutureDate(this.state.newFutureDate) || isClearing
                const serviceDate = serviceDateLabel(record.service_provided_date)

                return (
                    <div>
                        <div
                            style={{
                                color: `${CANCELLATION_STATUS_COLORS[record.status]}`,
                                fontWeight: 'bold'
                            }}
                        >
                            {CANCELLATION_STATUS_TYPES[record.status]}
                        </div>
                        {(isEditingFutureDate || futureCancellationDate) && (
                            <div>
                                {this.state.futureSpin === record.id ? (
                                    <Spin />
                                ) : isEditingFutureDate ? (
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                            <DatePicker
                                                format={'DD/MM/YYYY'}
                                                value={selectedFutureDate}
                                                onChange={e =>
                                                    this.setState({
                                                        newFutureDate: e ? e.format('YYYY-MM-DD 00:00:00') : ''
                                                    })
                                                }
                                                disabledDate={current => !!(current && current < moment().endOf('day'))}
                                                allowClear={false}
                                            />
                                            {this.canClearFutureDate(record) && this.state.newFutureDate && (
                                                <Icon
                                                    type="close"
                                                    className="clear-date-x"
                                                    onClick={() => this.setState({ newFutureDate: '' })}
                                                />
                                            )}
                                        </div>
                                        <div style={{ alignSelf: 'end' }}>
                                            <span style={{ cursor: 'pointer' }} onClick={this.cancelFutureEdit}>
                                                ביטול
                                            </span>
                                            <span
                                                style={{
                                                    marginRight: '7px',
                                                    color: !canSaveFutureDate ? 'grey' : 'inherit',
                                                    cursor: !canSaveFutureDate ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={
                                                    canSaveFutureDate ? () => this.saveFutureDate(record) : undefined
                                                }
                                            >
                                                שמירה
                                            </span>
                                        </div>
                                    </div>
                                ) : futureCancellationDate ? (
                                    <div style={{ display: 'flex' }}>
                                        <span>{`${FUTURE_CANCELLATION_DATE_LABEL}: ${futureCancellationDate.format(
                                            'DD/MM/YYYY'
                                        )}`}</span>
                                        {allowEdit && !isPaymentRestricted && (
                                            <span
                                                onClick={() => this.openFutureEdit(record)}
                                                style={{ marginRight: '7px', cursor: 'pointer' }}
                                            >
                                                <Icon type="edit" />
                                            </span>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {/* read-only, for every status, with or without a future-cancellation date */}
                        {serviceDate && (
                            <div style={{ display: 'flex' }}>
                                <span>{`${SERVICE_PROVIDED_DATE_LABEL}: ${serviceDate}`}</span>
                            </div>
                        )}
                        {allowEdit && (
                            <div style={{ display: 'flex', alignItems: 'center', width: '10px' }}>
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            {!isAgentsModerator && !isPaymentRestricted && (
                                                <Menu.Item
                                                    key={'0'}
                                                    onClick={() => this.handleStatusSelect('OPEN', record.id)}
                                                >
                                                    פתיחת בקשה
                                                </Menu.Item>
                                            )}
                                            {!isAgentsModerator && (
                                                <Menu.Item
                                                    key={'1'}
                                                    onClick={() => this.handleStatusSelect('IN_PROGRESS', record.id)}
                                                >
                                                    בטיפול
                                                </Menu.Item>
                                            )}
                                            {!isPaymentRestricted && (
                                                <Menu.Item
                                                    key={'2'}
                                                    onClick={() => this.handleStatusSelect('HANDLE_REQUIRE', record.id)}
                                                >
                                                    נדרש טיפול
                                                </Menu.Item>
                                            )}
                                            <Menu.Item
                                                key={'3'}
                                                onClick={() => this.openFutureEdit(record, 'FUTURE_CANCELLATION')}
                                            >
                                                ביטול עתידי
                                            </Menu.Item>
                                            {canStartAwaitingPayment(record, safeRoles) && (
                                                <Menu.Item key={'5'} onClick={() => this.openAwaitingPayment(record)}>
                                                    {CANCELLATION_STATUS_TYPES.AWAITING_PAYMENT}
                                                </Menu.Item>
                                            )}
                                            {!isAgentsModerator && !isPaymentRestricted && (
                                                <Menu.Item
                                                    key={'4'}
                                                    onClick={() => this.handleStatusSelect('CLOSED', record.id)}
                                                >
                                                    סגירת בקשה
                                                </Menu.Item>
                                            )}
                                        </Menu>
                                    }
                                    trigger={['click']}
                                >
                                    <Button>
                                        <span>
                                            <span>עדכון</span>
                                            <Icon
                                                type="down"
                                                style={{
                                                    position: 'relative',
                                                    right: '6px',
                                                    fontSize: '16px'
                                                }}
                                            />
                                        </span>
                                    </Button>
                                </Dropdown>
                                <div>{this.state.statusSpin === record.id && <Spin />}</div>
                            </div>
                        )}
                    </div>
                )
            }
        },
        {
            title: 'הליך משפטי',
            key: 'in_legal_proceedings',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.in_legal_proceedings,
            align: 'center',
            render: (_, record) => {
                const value = Number(record.in_legal_proceedings) === 1
                const safeRoles = this.props.authStore.roles || []
                const allowEdit =
                    safeRoles.includes('admin') || safeRoles.includes('moderator') || safeRoles.includes('money')

                if (this.state.legalSpin === record.id) return <Spin />
                if (allowEdit) {
                    return <Checkbox checked={value} onChange={e => this.handleLegalToggle(record, e.target.checked)} />
                }
                return value ? <Icon type="check" style={{ color: 'green' }} /> : <div>{'-'}</div>
            }
        },
        {
            title: 'הוסבר ללקוח',
            key: 'is_cancel_explained',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.is_cancel_explained,
            align: 'center',
            render: (_, record) => {
                if (this.state.explainSaving[record.id]) return <Spin />
                const checked = isCancelExplained(record)
                if (!canEditCancelExplained(this.props.authStore.roles)) return <Checkbox checked={checked} disabled />
                return (
                    <Checkbox checked={checked} onChange={e => this.handleExplainedToggle(record, e.target.checked)} />
                )
            }
        },
        {
            title: 'נדרש תשלום',
            key: 'is_payment_required',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.is_payment_required,
            align: 'center',
            render: (_, record) => {
                const paidAt = record.awaiting_paid_at ? moment(record.awaiting_paid_at) : null
                if (Number(record.is_payment_required) !== 1) {
                    if (paidAt && paidAt.isValid()) return <div>{`שולם ב ${paidAt.format('DD/MM/YYYY')}`}</div>
                    return <Icon type="close" style={{ color: 'red' }} />
                }

                const safeRoles = this.props.authStore.roles || []
                const allowEdit = safeRoles.includes('admin') || safeRoles.includes('money')
                if (!allowEdit) return <Checkbox checked={false} disabled />

                const { payingId, paymentDate, paymentSpin } = this.state
                if (paymentSpin === record.id) return <Spin />

                const isEditing = payingId === record.id
                const canSave = this.isValidPaymentDate(paymentDate)
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Checkbox checked={isEditing} onChange={e => this.openPaymentEdit(record, e.target.checked)}>
                            התקבל תשלום
                        </Checkbox>
                        {isEditing && (
                            <>
                                <DatePicker
                                    format={'DD/MM/YYYY'}
                                    placeholder="תאריך תשלום"
                                    value={paymentDate ? moment(paymentDate) : null}
                                    onChange={e =>
                                        this.setState({ paymentDate: e ? e.format('YYYY-MM-DD 00:00:00') : '' })
                                    }
                                    disabledDate={current => !!(current && current > moment().endOf('day'))}
                                    allowClear={false}
                                />
                                <div style={{ alignSelf: 'end' }}>
                                    <span
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => this.openPaymentEdit(record, false)}
                                    >
                                        ביטול
                                    </span>
                                    <span
                                        style={{
                                            marginRight: '7px',
                                            color: !canSave ? 'grey' : 'inherit',
                                            cursor: !canSave ? 'not-allowed' : 'pointer'
                                        }}
                                        onClick={canSave ? () => this.savePayment(record) : undefined}
                                    >
                                        שמירה
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                )
            }
        },
        {
            title: 'עודכן ע״י',
            key: 'updated_by',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.updated_by,
            align: 'center',
            render: (_, record) => <div>{record.updated_by || '-'}</div>
        },
        {
            title: 'מכותבים',
            key: 'recipients',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.recipients,
            align: 'right',
            render: (_, record) => {
                const info = {
                    full_name: `${record.requested_for_first_name} ${record.requested_for_last_name}`,
                    requested_for_id: record.requested_for,
                    users: this.props.recipients,
                    reqId: record.id,
                    existingRecipients: { recipients: this.getRecipients(record) },
                    onClose: () => this.setState({ show: null })
                }

                let style = {
                    display: `${this.state.show === record.id ? 'flex' : 'none'}`
                }

                return (
                    <>
                        <Button onClick={() => this.setState({ show: record.id })}>מכותבים</Button>
                        <div style={style}>
                            <Recipients info={info} />
                        </div>
                    </>
                )
            }
        }
    ]

    adminColumns = [
        {
            title: 'יצירת חיוב',
            key: 'new_commission',
            width: CANCELLATION_TABLE_COLUMN_WIDTHS.new_commission,
            align: 'right',
            render: (_, record) => {
                const agentInfo = {
                    first_name: record.agent_first_name,
                    last_name: record.agent_last_name,
                    id: record.agent_id,
                    percents: record.agent_percents,
                    isSubAgent: record.isSubAgent
                }
                const commissions = '[]' // record.agent_client_commissions
                const client_id = record.requested_for
                const { subscription_id, special_subscription_id } = record

                const info = {
                    agentInfo,
                    commissions,
                    client_id,
                    subscription_id,
                    special_subscription_id
                }

                return (
                    <Button onClick={() => this.props.popupStore.togglePopup('newCommissionFromCancellation', info)}>
                        יצירת חיוב
                    </Button>
                )
            }
        }
    ]

    render() {
        const {
            authStore: { roles, id },
            cancellationRequestStore: { pagination, paginate, loadOnPaginate }
        } = this.props

        let showColumns = this.columns
        let tableScrollX = CANCELLATION_TABLE_SCROLL_X_WITHOUT_ADMIN
        const safeRoles = roles || []
        if (safeRoles.includes('admin') || safeRoles.includes('moderator') || safeRoles.includes('money')) {
            showColumns = this.columns.concat(this.adminColumns)
            tableScrollX = CANCELLATION_TABLE_SCROLL_X
        }

        return (
            <>
                <div className="cancellation-requests-table-shell">
                    <Table
                        columns={showColumns}
                        dataSource={this.props.list}
                        rowKey={this.getRowKey}
                        rowClassName={record =>
                            this.getRecipients(record).find(recId => `${recId}` === `${id}`)
                                ? styleClass.highLightRow
                                : ''
                        }
                        size="small"
                        className="cancellation-requests-table"
                        scroll={{ x: tableScrollX, y: CANCELLATION_TABLE_SCROLL_Y }}
                        bordered={true}
                        locale={{ emptyText: 'לא נמצאו בקשות' }}
                        pagination={false}
                    />
                    <div className="cancellation-requests-table-pagination">
                        <Pagination
                            pageSize={pagination.perPage}
                            current={pagination.page}
                            total={pagination.total}
                            showTotal={total => `סה״כ ${total} בקשות`}
                            onChange={(page, perPage) => paginate({ page, perPage })}
                        />
                        {loadOnPaginate ? <Spin style={{ marginRight: '20px' }} /> : ''}
                    </div>
                </div>
                <Modal
                    title={CANCELLATION_STATUS_TYPES.AWAITING_PAYMENT}
                    visible={!!this.state.awaitingRecord}
                    onCancel={this.closeAwaitingPayment}
                    onOk={this.saveAwaitingPayment}
                    okText="אישור"
                    cancelText="ביטול"
                    okButtonProps={{ disabled: !this.canSaveAwaitingPayment() }}
                    confirmLoading={this.state.awaitingSpin}
                    destroyOnClose
                >
                    <div style={{ direction: 'rtl', textAlign: 'right' }}>
                        <div style={{ marginBottom: '6px' }}>{`${FUTURE_CANCELLATION_DATE_LABEL} *`}</div>
                        <DatePicker
                            format={'DD/MM/YYYY'}
                            value={this.state.awaitingDate ? moment(this.state.awaitingDate) : null}
                            onChange={e => this.setState({ awaitingDate: e ? e.format('YYYY-MM-DD 00:00:00') : '' })}
                            disabledDate={current => !!(current && current < moment().endOf('day'))}
                            style={{ width: '100%', marginBottom: '15px', border: 'none' }}
                            allowClear={false}
                        />
                        {/* a prefilled scheduled date that is already overdue never passes the save gate */}
                        {this.state.awaitingDate && !this.isFutureDate(this.state.awaitingDate) && (
                            <div style={{ color: 'red', marginTop: '-10px', marginBottom: '15px' }}>
                                {'יש לבחור תאריך עתידי'}
                            </div>
                        )}
                        <div style={{ marginBottom: '6px' }}>{'תאריך צריכת שירות *'}</div>
                        <DatePicker
                            format={'DD/MM/YYYY'}
                            value={
                                this.state.awaitingServiceDate
                                    ? moment(this.state.awaitingServiceDate, 'YYYY-MM-DD')
                                    : null
                            }
                            onChange={e => this.setState({ awaitingServiceDate: e ? e.format('YYYY-MM-DD') : '' })}
                            disabledDate={current => !!(current && current > moment().endOf('day'))}
                            style={{ width: '100%', marginBottom: '15px', border: 'none' }}
                            allowClear={false}
                        />
                        <div style={{ marginBottom: '6px' }}>{'תיעוד *'}</div>
                        <Input.TextArea
                            rows={4}
                            value={this.state.awaitingDoc}
                            onChange={e => this.setState({ awaitingDoc: e.target.value })}
                            style={{ direction: 'rtl' }}
                        />
                    </div>
                </Modal>
            </>
        )
    }
}
